import { CaretDownIcon } from '@phosphor-icons/react';

import OrderStatusIcon from '@/assets/images/order-change-status-icon.webp';
import { ActionMenu } from '@/components/common/action-menu';
import { useConfirm, useDestructiveAction } from '@/components/feedback/dialog';
import { useToast } from '@/components/feedback/toast';
import type { OrderDetail, OrderStatus } from '@/features/orders';
import {
  INSTALLATION_PENDING_MESSAGE,
  ORDER_CANCELLED_MESSAGE,
  ORDER_STATUS_CHIP,
  ORDER_STATUS_UPDATED_MESSAGE,
  STATUS_TRANSITIONS,
  isFinalStatus,
  useCancelOrder,
  useUpdateOrderStatus,
} from '@/features/orders';

import styles from './order-status-control.module.scss';

export function OrderStatusControl({ order }: { order: OrderDetail }) {
  const { confirm } = useConfirm();
  const { run } = useDestructiveAction();
  const toast = useToast();
  const updateStatus = useUpdateOrderStatus(order.id);
  const cancelOrder = useCancelOrder(order.id);
  const chip = ORDER_STATUS_CHIP[order.status];

  if (isFinalStatus(order.status)) {
    return <span className={`${styles.pill} ${styles.static}`}>{chip.label}</span>;
  }

  const selectStatus = (target: OrderStatus) => {
    if (target === 'cancelled') {
      void confirm({
        title: 'Cancel this Order?',
        subtitle: (
          <span className={styles.cancelSubtitle}>
            Are you sure you want to cancel this order? Once done refund will be initiated.
          </span>
        ),
        confirmText: 'Cancel Order & Refund',
        cancelText: 'Keep Order',
        danger: true,
        onConfirm: async () => {
          await run(() => cancelOrder.mutateAsync(), {
            success: ORDER_CANCELLED_MESSAGE,
            errorFallback: 'Failed to cancel the order.',
          });
        },
      });
      return;
    }
    // "Awaiting an admin decision" is Installation status **open** (CONTEXT.md), which is what
    // ADR-0015's "blocked while the installation request is pending" means. This compared against
    // 'pending' — a token the backend never sends for an installation request — so the guard never
    // fired and the block was left entirely to the server's 409 (ADO 117331). Copy/code stay
    // `INSTALLATION_PENDING_*`: those are shared with the server so the wording can't drift.
    if (target === 'delivered' && order.installationRequest?.status === 'open') {
      toast.error({ title: INSTALLATION_PENDING_MESSAGE });
      return;
    }
    void confirm({
      title: 'Change order status?',
      subtitle: (
        <span className={styles.subtitle}>
          The new order status will be visible to the customer in the webstore.
        </span>
      ),
      confirmText: 'Change Status',
      icon: <img src={OrderStatusIcon} alt="Order Status" className={styles.icon} />,
      onConfirm: async () => {
        await run(() => updateStatus.mutateAsync(target), {
          success: ORDER_STATUS_UPDATED_MESSAGE,
          errorFallback: 'Failed to update order status.',
        });
      },
    });
  };

  const items = STATUS_TRANSITIONS[order.status].map((target) => ({
    key: target,
    label: ORDER_STATUS_CHIP[target].label,
    danger: target === 'cancelled',
    onClick: () => selectStatus(target),
  }));

  return (
    <ActionMenu groups={[{ items }]}>
      <button type="button" className={styles.pill} aria-label="Change order status">
        {chip.label}
        <CaretDownIcon size={16} aria-hidden />
      </button>
    </ActionMenu>
  );
}
